// SPDX-License-Identifier: AGPL-3.0-or-later

import {BoostGemIcon} from '@app/features/boost/components/BoostGemIcon';
import styles from '@app/features/boost/components/BoostSubscription.module.css';
import {formatPayable} from '@app/features/shop/utils/ShopPriceFormat';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import type {ShopChargeCurrency} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {MinusIcon, PlusIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';

const PRODUCT_DESCRIPTOR = msg({
	message: '{count, plural, one {Server Boost} other {Server Boosts}}',
	comment: 'Product name in the boost subscription dialog, beside the quantity picker. {count} is how many boosts.',
});
const UNIT_PER_MONTH_DESCRIPTOR = msg({
	message: '{amount} / month',
	comment: 'Price of one boost under its name in the boost subscription dialog, e.g. "₺49,99 / month".',
});
const UNIT_EACH_DESCRIPTOR = msg({
	message: '{amount} each · 30 days',
	comment:
		'Price of one boost under its name in the one-time boost buy dialog, e.g. "49,99 TL each · 30 days": each boost is a 30 day slot.',
});
const SUBTOTAL_DESCRIPTOR = msg({
	message: 'Subtotal',
	comment: 'Row label in the boost subscription dialog: the monthly total of all chosen boosts.',
});
const SUBTOTAL_PER_MONTH_DESCRIPTOR = msg({
	message: '{amount} / mo',
	comment: 'The monthly total of the chosen boosts in the boost subscription dialog, e.g. "₺249,95 / mo".',
});
const QUANTITY_DESCRIPTOR = msg({
	message: 'Number of boosts',
	comment: 'Screen reader name of the quantity picker in the boost subscription dialog.',
});
const ADD_DESCRIPTOR = msg({
	message: 'Add a boost',
	comment: 'Screen reader name of the plus button of the quantity picker in the boost subscription dialog.',
});
const REMOVE_DESCRIPTOR = msg({
	message: 'Remove a boost',
	comment: 'Screen reader name of the minus button of the quantity picker in the boost subscription dialog.',
});

// Where the dialog is: the step names in order, the current one marked.
export const BoostSteps = ({steps, current}: {steps: ReadonlyArray<string>; current: number}) => (
	<ol className={styles.steps} data-flx="boost.steps">
		{steps.map((name, index) => (
			<li
				key={name}
				className={clsx(index === current && styles.stepCurrent)}
				aria-current={index === current ? 'step' : undefined}
			>
				{name}
			</li>
		))}
	</ol>
);

interface BoostQuantityStepProps {
	quantity: number;
	max: number;
	// The price of one boost each month, in the minor unit of the currency; the subtotal is this times the quantity.
	unitMinor: number;
	currency: ShopChargeCurrency;
	onChange: (quantity: number) => void;
	disabled?: boolean;
	// The steps of the dialog above the picker, the first one being this one.
	steps?: ReadonlyArray<string>;
	// One-time 30 day boosts instead of a monthly subscription: no "/ month" on the prices.
	oneTime?: boolean;
	// How an amount shows; absent: the currency's own symbol.
	format?: (minor: number) => string;
	children?: React.ReactNode;
}

// How many boosts, of a monthly subscription or bought once: − N +, the price of one, and the subtotal.
export const BoostQuantityStep = ({
	quantity,
	max,
	unitMinor,
	currency,
	onChange,
	disabled = false,
	steps,
	oneTime = false,
	format,
	children,
}: BoostQuantityStepProps) => {
	const {i18n} = useLingui();
	const set = (next: number) => {
		if (!disabled && next >= 1 && next <= max && next !== quantity) onChange(next);
	};
	const onKeyDown = (event: React.KeyboardEvent) => {
		const next =
			event.key === 'ArrowUp' || event.key === 'ArrowRight'
				? quantity + 1
				: event.key === 'ArrowDown' || event.key === 'ArrowLeft'
					? quantity - 1
					: event.key === 'Home'
						? 1
						: event.key === 'End'
							? max
							: null;
		if (next === null) return;
		event.preventDefault();
		set(next);
	};
	const money = format ?? ((minor: number) => formatPayable(minor, currency, i18n.locale));
	const step = (delta: 1 | -1) => {
		const off = disabled || quantity + delta < 1 || quantity + delta > max;
		return (
			<FocusRing offset={-2}>
				<button
					type="button"
					tabIndex={-1}
					className={styles.stepButton}
					aria-label={i18n._(delta > 0 ? ADD_DESCRIPTOR : REMOVE_DESCRIPTOR)}
					aria-disabled={off}
					onClick={() => set(quantity + delta)}
					data-flx={delta > 0 ? 'boost.quantity-step.add' : 'boost.quantity-step.remove'}
				>
					{delta > 0 ? <PlusIcon weight="bold" /> : <MinusIcon weight="bold" />}
				</button>
			</FocusRing>
		);
	};
	return (
		<div className={styles.step} data-flx="boost.quantity-step">
			{steps && <BoostSteps steps={steps} current={0} />}
			<div className={styles.product} data-flx="boost.quantity-step.product">
				<span className={styles.gem}>
					<BoostGemIcon size={24} />
				</span>
				<div className={styles.productText}>
					<p className={styles.productName}>{i18n._(PRODUCT_DESCRIPTOR, {count: quantity})}</p>
					<p className={styles.unit}>
						{i18n._(oneTime ? UNIT_EACH_DESCRIPTOR : UNIT_PER_MONTH_DESCRIPTOR, {amount: money(unitMinor)})}
					</p>
				</div>
				<div className={styles.stepper} data-flx="boost.quantity-step.stepper">
					{step(-1)}
					<FocusRing offset={-2}>
						<span
							role="spinbutton"
							tabIndex={disabled ? -1 : 0}
							className={styles.count}
							aria-label={i18n._(QUANTITY_DESCRIPTOR)}
							aria-valuenow={quantity}
							aria-valuemin={1}
							aria-valuemax={max}
							aria-disabled={disabled}
							onKeyDown={onKeyDown}
							data-flx="boost.quantity-step.count"
						>
							{quantity}
						</span>
					</FocusRing>
					{step(1)}
				</div>
			</div>
			<div className={styles.subtotal} data-flx="boost.quantity-step.subtotal">
				<span>{i18n._(SUBTOTAL_DESCRIPTOR)}</span>
				<span className={styles.subtotalAmount}>
					{oneTime
						? money(unitMinor * quantity)
						: i18n._(SUBTOTAL_PER_MONTH_DESCRIPTOR, {amount: money(unitMinor * quantity)})}
				</span>
			</div>
			{children}
		</div>
	);
};
